import { useEffect, useId, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

/** Shared anchor state; the help affordance supplies its existing close delay. */
export function useTooltipAnchor(closeDelayMs = 0) {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClose = () => {
    if (closeTimer.current !== null) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };
  const onOpenChange = (next: boolean) => { cancelClose(); setOpen(next); };
  const show = () => onOpenChange(true);
  const hide = () => {
    cancelClose();
    if (closeDelayMs === 0) setOpen(false);
    else closeTimer.current = setTimeout(() => { closeTimer.current = null; setOpen(false); }, closeDelayMs);
  };
  // Leaving by focus closes one task later, once the next element holds focus. Closing on the blur itself
  // unmounts the tip while the browser has focus on <body>, and the focus trap of an enclosing dialog (Radix
  // FocusScope) answers a removed node with focus on <body> by focusing the dialog: Tab from a mark in
  // Settings jumped back to the top of the page. A focus that returns first cancels the close.
  const hideAfterFocusMoves = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => { closeTimer.current = null; setOpen(false); }, closeDelayMs);
  };
  useEffect(() => () => {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
  }, []);
  return {
    open, tooltipId, onOpenChange, show, hide,
    // Hover belongs to a MOUSE only. A tap's compatibility mouse events (the enter/leave pair the browser
    // synthesises around a touch click) are aimed at whatever now sits under the finger, and the tip the tap
    // has just opened is exactly what lands there: the browser sends the anchor a `mouseleave` and the tip
    // closes itself inside the same gesture. Which of the two orders wins is decided by timing and layout, so
    // the same tap named the mark or said nothing at all. A tap opens the tip through its focus and its click,
    // and the next press outside the anchor puts it away.
    hover: {
      onPointerEnter: (event: ReactPointerEvent) => { if (event.pointerType === 'mouse') show(); },
      onPointerLeave: (event: ReactPointerEvent) => { if (event.pointerType === 'mouse') hide(); },
    },
    focus: { onFocus: show, onBlur: hideAfterFocusMoves },
  };
}
